import React from 'react';
import { useIntl } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { EmojiTitleStateContainer } from '@confluence/emoji-title/entry-points/emojiTitleStateContainer';
import { CoverPictureStateContainer } from '@confluence/page-cover-picture';

import { SaveIndicatorComponent } from './SaveIndicatorComponent';
import type { SaveIndicatorProps } from './types';
import { SaveIndicatorComponentExperiment } from './SaveIndicatorComponentExperiment';

enum EXPERIMENTAL_SAVE_INDICATOR_GROUPS {
	CONTROL = 'Control',
	VARIANT1 = 'Variant1',
}

const SaveIndicatorWrapper = ({ editorActions, saveError, title }: SaveIndicatorProps) => {
	const { formatMessage } = useIntl();
	const isInMoreNoticeableSaveIndicatorExperiment =
		FeatureGates.getExperimentValue<string>(
			'cc_more_noticeable_save_indicator',
			'cohort',
			EXPERIMENTAL_SAVE_INDICATOR_GROUPS.CONTROL,
		) === EXPERIMENTAL_SAVE_INDICATOR_GROUPS.VARIANT1;
	return (
		<Subscribe to={[EmojiTitleStateContainer, CoverPictureStateContainer]}>
			{(
				emojiTitleState: EmojiTitleStateContainer,
				coverPictureState: CoverPictureStateContainer,
			) => {
				const selectingEmojiTitle = emojiTitleState.state.isUpdatingEmoji;
				const selectingCoverPicture =
					coverPictureState.state.isUploadingPicture ||
					coverPictureState.state.isRemovingPicture ||
					coverPictureState.state.isRepositioningPicture;
				const hasEmoji = !!emojiTitleState.state.selectedEmoji;
				const hasCoverPicture = coverPictureState.state.hasCoverPicture;
				return isInMoreNoticeableSaveIndicatorExperiment ? (
					<SaveIndicatorComponentExperiment
						editorActions={editorActions}
						saveError={saveError}
						title={title}
						hasEmoji={hasEmoji}
						hasCoverPicture={hasCoverPicture}
						selectingEmojiTitle={selectingEmojiTitle}
						selectingCoverPicture={selectingCoverPicture}
					/>
				) : (
					<SaveIndicatorComponent
						editorActions={editorActions}
						saveError={saveError}
						title={title}
						hasEmoji={hasEmoji}
						hasCoverPicture={hasCoverPicture}
						selectingEmojiTitle={selectingEmojiTitle}
						selectingCoverPicture={selectingCoverPicture}
						formatMessage={formatMessage}
					/>
				);
			}}
		</Subscribe>
	);
};

export const SaveIndicator = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_MODES,
})(SaveIndicatorWrapper);
